import { useState, useMemo, useEffect } from "react";
import { listFilterTemplates } from "../../common/services/filterTemplatesApi";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { useProjectsData } from "../hooks/useProjectsData";
import CustomerDrawer from "../components/CustomerDrawer";
import ProjectDrawer from "../components/ProjectDrawer";
import CreateTasksModal from "../components/CreateTasksModal";
import FilterTriggerButton from "../../common/components/FilterTriggerButton";
import ChoiceFilterPopover from "../../common/components/ChoiceFilterPopover";
import {
  custStatusPill,
  projStatusPill,
  filterCustomers,
  filterProjects,
  DEFAULT_CUSTOMER_FILTERS,
  DEFAULT_PROJECT_FILTERS,
  buildCustFilterFields,
  buildProjFilterFields,
  applyCustomerFilters,
  applyProjectFilters,
  countActiveFilters,
} from "../services/projectsViewDerivations";
import "../styles/ProjectsViewV2.css";

const TABS = [
  { key: "customers", label: "Customers" },
  { key: "projects",  label: "Projects"  },
];

export default function ProjectsViewV2Page({ showToast, user }) {
  const isAdmin = ["Admin", "Super Admin"].includes(user?.db_role);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [tab,             setTab]             = useState("customers");
  const [search,          setSearch]          = useState("");
  const [custDrawer,      setCustDrawer]      = useState(null);
  const [projDrawer,      setProjDrawer]      = useState(null);
  const [createTasksProj, setCreateTasksProj] = useState(null);
  const [filterOpen,      setFilterOpen]      = useState(false);

  const [custFilters,    setCustFilters]    = useState(DEFAULT_CUSTOMER_FILTERS);
  const [custFilterMode, setCustFilterMode] = useState("all");
  const [projFilters,    setProjFilters]    = useState(DEFAULT_PROJECT_FILTERS);
  const [projFilterMode, setProjFilterMode] = useState("all");

  useEffect(() => {
    listFilterTemplates("projects_customer").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) { setCustFilters(def.filter_def || {}); setCustFilterMode(def.filter_mode || "all"); }
    }).catch(() => {});
    listFilterTemplates("projects_project").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) { setProjFilters(def.filter_def || {}); setProjFilterMode(def.filter_mode || "all"); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { customers, projects, users, loading, reload } = useProjectsData({ showToast });

  const q = search.trim();

  const custFilterFields = useMemo(() => buildCustFilterFields(customers), [customers]);
  const projFilterFields = useMemo(() => buildProjFilterFields(projects, customers), [projects, customers]);

  const visibleCustomers = useMemo(
    () => applyCustomerFilters(filterCustomers(customers, q), custFilters, custFilterMode),
    [customers, q, custFilters, custFilterMode]
  );
  const visibleProjects = useMemo(
    () => applyProjectFilters(filterProjects(projects, q), projFilters, projFilterMode),
    [projects, q, projFilters, projFilterMode]
  );

  const custActiveCount = countActiveFilters(custFilters);
  const projActiveCount = countActiveFilters(projFilters);
  const activeFilterCount = tab === "customers" ? custActiveCount : projActiveCount;

  function handleTabChange(key) {
    setTab(key);
    setSearch("");
    setFilterOpen(false);
  }

  function handleSaved() {
    reload();
    setCustDrawer(null);
    setProjDrawer(null);
  }

  function handleFilterApply(draft, mode) {
    if (tab === "customers") {
      setCustFilters(draft);
      setCustFilterMode(mode);
    } else {
      setProjFilters(draft);
      setProjFilterMode(mode);
    }
  }

  function handleFilterClear() {
    if (tab === "customers") {
      setCustFilters({});
      setCustFilterMode("all");
    } else {
      setProjFilters({});
      setProjFilterMode("all");
    }
  }

  const count = tab === "customers" ? visibleCustomers.length : visibleProjects.length;

  return (
    <div
      className="pjv2-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(c => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="pjv2-main">
        {/* ── Page header ── */}
        <header className="page-head">
          <h1>
            Projects{" "}
            <em>{loading ? "loading…" : `${customers.length} customers · ${projects.length} projects`}</em>
          </h1>
          <div className="page-head-actions">
            <button
              className="v2-btn-primary"
              onClick={() => tab === "customers" ? setCustDrawer("new") : setProjDrawer("new")}
            >
              <span className="plus">+</span>
              New {tab === "customers" ? "Customer" : "Project"}
            </button>
          </div>
        </header>

        {/* ── Tab bar + toolbar-right ── */}
        <div className="page-toolbar">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              className={`page-tab${tab === key ? " page-tab--active" : ""}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
              <span className="page-tab-count">
                {key === "customers" ? customers.length : projects.length}
              </span>
            </button>
          ))}

          <div className="page-toolbar-right">
            <div className="cmn-toolbar-search">
              <svg className="cmn-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className="cmn-search-input"
                placeholder={tab === "customers" ? "Search customers…" : "Search projects…"}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <FilterTriggerButton
              activeCount={activeFilterCount}
              onClick={() => setFilterOpen(true)}
              onClear={handleFilterClear}
            />
            <span className="pjv2-count">
              {count} {tab === "customers" ? "customer" : "project"}{count !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="pjv2-tbl-wrap">
          {loading && <div className="pjv2-state">Loading…</div>}

          {!loading && tab === "customers" && (
            <>
              <div className="pjv2-tbl-hd">
                <span className="pjv2-tbl-hd-num">#</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 200px" }}>Customer</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 100px" }}>Code</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: 1 }}>Account Manager</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 90px" }}>Status</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 80px" }}>Projects</span>
              </div>
              {visibleCustomers.length === 0 && (
                <div className="pjv2-state">{q ? "No matching customers." : "No customers match current filters."}</div>
              )}
              {visibleCustomers.map((c, i) => {
                const projCount = projects.filter(p => p.customer_id === c.id).length;
                return (
                  <div key={c.id} className="pjv2-tbl-row" onClick={() => setCustDrawer(c)}>
                    <span className="pjv2-tbl-num">{i + 1}</span>
                    <span className="pjv2-tbl-cell pjv2-tbl-cell--primary" style={{ flex: "0 0 200px" }}>{c.customer_name}</span>
                    <span className="pjv2-tbl-cell pjv2-tbl-cell--muted"  style={{ flex: "0 0 100px" }}>{c.customer_code || "—"}</span>
                    <span className="pjv2-tbl-cell"                        style={{ flex: 1 }}>{c.account_manager_name || "—"}</span>
                    <span className="pjv2-tbl-cell"                        style={{ flex: "0 0 90px" }}>
                      <span className={`pjv2-pill ${custStatusPill(c.status)}`}>{c.status}</span>
                    </span>
                    <span className="pjv2-tbl-cell pjv2-tbl-cell--muted"  style={{ flex: "0 0 80px" }}>{projCount}</span>
                  </div>
                );
              })}
            </>
          )}

          {!loading && tab === "projects" && (
            <>
              <div className="pjv2-tbl-hd">
                <span className="pjv2-tbl-hd-num">#</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 220px" }}>Project</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 90px" }}>Code</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: 1 }}>Customer</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: 1 }}>Account Manager</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 90px" }}>Status</span>
                <span className="pjv2-tbl-hd-cell" style={{ flex: "0 0 120px" }}>Actions</span>
              </div>
              {visibleProjects.length === 0 && (
                <div className="pjv2-state">{q ? "No matching projects." : "No projects match current filters."}</div>
              )}
              {visibleProjects.map((p, i) => (
                <div key={p.id} className="pjv2-tbl-row" onClick={() => setProjDrawer(p)}>
                  <span className="pjv2-tbl-num">{i + 1}</span>
                  <span className="pjv2-tbl-cell pjv2-tbl-cell--primary" style={{ flex: "0 0 220px" }}>{p.project_name}</span>
                  <span className="pjv2-tbl-cell pjv2-tbl-cell--muted"  style={{ flex: "0 0 90px"  }}>{p.project_code || "—"}</span>
                  <span className="pjv2-tbl-cell"                        style={{ flex: 1 }}>{p.customer_name || "—"}</span>
                  <span className="pjv2-tbl-cell"                        style={{ flex: 1 }}>{p.account_manager_name || "—"}</span>
                  <span className="pjv2-tbl-cell"                        style={{ flex: "0 0 90px" }}>
                    <span className={`pjv2-pill ${projStatusPill(p.status)}`}>{p.status}</span>
                  </span>
                  <span className="pjv2-tbl-cell pjv2-tbl-cell--actions" style={{ flex: "0 0 120px" }}>
                    <button
                      className="pjv2-row-action-btn"
                      onClick={e => { e.stopPropagation(); setCreateTasksProj(p); }}
                    >
                      Create Tasks
                    </button>
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── Filter panel ── */}
      <ChoiceFilterPopover
        open={filterOpen}
        title={tab === "customers" ? "Customer Filters" : "Project Filters"}
        fields={tab === "customers" ? custFilterFields : projFilterFields}
        appliedFilters={tab === "customers" ? custFilters : projFilters}
        filterMode={tab === "customers" ? custFilterMode : projFilterMode}
        module={tab === "customers" ? "projects_customer" : "projects_project"}
        onApply={handleFilterApply}
        onClose={() => setFilterOpen(false)}
      />

      {/* ── Drawers ── */}
      {custDrawer !== null && (
        <CustomerDrawer
          item={custDrawer === "new" ? null : custDrawer}
          projects={projects}
          users={users}
          isAdmin={isAdmin}
          onClose={() => setCustDrawer(null)}
          onSaved={handleSaved}
          showToast={showToast}
        />
      )}

      {projDrawer !== null && (
        <ProjectDrawer
          item={projDrawer === "new" ? null : projDrawer}
          customers={customers}
          users={users}
          onClose={() => setProjDrawer(null)}
          onSaved={handleSaved}
          showToast={showToast}
        />
      )}

      {/* ── Create Tasks Modal ── */}
      {createTasksProj && (
        <CreateTasksModal
          project={createTasksProj}
          onClose={() => setCreateTasksProj(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
}
